import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { PERIOD_LABELS, bucketLabel, type DashboardResponse, type Period } from "@/lib/dashboard";
import { Link } from "react-router-dom";
import { wholeMoney, money } from "@/lib/format";
import { MsiCommitmentsNote } from "@/components/MsiCommitmentsNote";

export function Kpis({ d }: { d: DashboardResponse }) {
  const f = d.month_flow;
  const planSpend = d.planned_expense - (d.planned_debt ?? 0);
  const over = planSpend > 0 && f.spending > planSpend;
  const cards: [string, string, string, string, string][] = [
    ["kpi-balance", "Balance del mes", `${f.balance >= 0 ? "+" : ""}${money(f.balance)}`, f.balance < 0 ? "text-[#9C3D24]" : "text-[#1A1F2B]", "Lo que entró menos lo que gastaste y pagaste de deudas"],
    ["kpi-income", "Ingresos", money(f.income), "text-[#2C4F91]", d.planned_income ? `de ${money(d.planned_income)} que esperas este mes` : "Arma tu presupuesto para comparar"],
    ["kpi-expense", "Gastos", money(f.spending), over ? "text-[#B23A1E]" : "text-[#1A1F2B]", `Esenciales y Comodidad${planSpend > 0 ? ` · de ${money(planSpend)} de tu presupuesto` : ""}`],
  ];
  return (
    <div className="grid overflow-hidden rounded-[14px] border border-[#DDE1E8] bg-white sm:grid-cols-3">
      {cards.map(([id, l, v, c, sub], i) => (
        <div key={id} className={`p-5 sm:p-6 ${i ? "border-t border-[#DDE1E8] sm:border-l sm:border-t-0" : ""}`} data-testid={id}>
          <PanelLabel>{l}</PanelLabel>
          <p className={`figure-lg mt-2 ${c}`} data-testid={`${id}-value`}>{v}</p>
          <p className="mt-1 text-sm text-slate-500">{sub}</p>
          {id === "kpi-expense" && f.debt > 0 && <p className="mt-1 text-sm font-semibold text-[#9C3D24]" data-testid="kpi-expense-debt">más {money(f.debt)} en deudas</p>}
          {id === "kpi-balance" && f.savings > 0 && <p className="mt-1 text-sm font-semibold text-[#2C4F91]" data-testid="kpi-balance-savings">Además apartaste {money(f.savings)} a ahorro e inversión</p>}
          {id === "kpi-expense" && <MsiCommitmentsNote c={d.msi_commitments} testId="dashboard-msi" text={(t) => `${t} de compras a meses se pagan en los próximos meses`} />}
          {id === "kpi-expense" && (d.unrecorded_diff ?? 0) > 0 && (
            <Link to={`/movimientos?type=adjustment&month=${d.month}`} className="mt-3 block rounded-lg bg-[#FBEAE5] px-3 py-2 text-sm font-semibold text-[#8E2E18] transition-colors hover:bg-[#F7DCD3]" data-testid="dashboard-unrecorded-diff">
              Diferencias sin registrar este mes: {money(d.unrecorded_diff ?? 0)} <span className="font-normal underline underline-offset-2">Ver ajustes</span>
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

export function PeriodTabs({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return (
    <div className="flex gap-1 rounded-lg bg-[#F4F5F7] p-1" data-testid="period-tabs">
      {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
        <button key={p} onClick={() => onChange(p)} data-testid={`period-${p}`}
          className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${value === p ? "bg-white text-[#1B2A4A] shadow-sm" : "text-slate-500 hover:text-[#1B2A4A]"}`}>{PERIOD_LABELS[p]}</button>
      ))}
    </div>
  );
}

export function IncomeExpenseChart({ d, period, onPeriod }: { d: DashboardResponse; period: Period; onPeriod: (p: Period) => void }) {
  const data = d.series.map((p) => ({ ...p, label: bucketLabel(p.key, period) }));
  const totals = d.series.reduce((a, p) => ({ i: a.i + p.income, e: a.e + p.expense }), { i: 0, e: 0 });
  return (
    <Panel testId="income-expense-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PanelLabel>Ingresos contra gastos</PanelLabel>
        <PeriodTabs value={period} onChange={onPeriod} />
      </div>
      <p className="tabular mt-3 text-xs text-slate-500">
        INGRESOS <b className="text-[#2C4F91]">{money(totals.i)}</b> · GASTOS <b className="text-[#1A1F2B]">{money(totals.e)}</b> · NETO <b className="text-[#1B2A4A]">{money(totals.i - totals.e)}</b>
      </p>
      <div className="mt-4 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#ECE3D3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5A6170" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={wholeMoney} tick={{ fontSize: 11, fill: "#5A6170" }} axisLine={false} tickLine={false} width={84} />
            <Tooltip formatter={(v) => money(Number(v))} contentStyle={{ borderRadius: 10, border: "1px solid #DDE1E8" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="#CBB898" />
            <Bar dataKey="net" name="Neto" fill="#9DB2D6" radius={[4, 4, 0, 0]} maxBarSize={32}>
              {data.map((p) => <Cell key={p.key} fill={p.net < 0 ? "#E8A797" : "#9DB2D6"} />)}
            </Bar>
            <Line type="monotone" dataKey="income" name="Ingresos" stroke="#1E6B3A" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="expense" name="Gastos" stroke="#A23B1F" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}
